import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { StatusBadge } from '@client/components/_ui/display/StatusBadge';
import { User } from '@client/components/_ui/display/User';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { ICONS } from '@client/constants/icons';
import { usePermissions } from '@client/hooks/auth/usePermissions';
import { useConfigServiceLoader } from '@client/hooks/business/configuration/useConfigServiceLoader';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { selectIsLiteOrg, selectOrg, selectUser, useAuthStore } from '@client/stores/AuthStore';
import { queryClient, type RouterInputs, type RouterOutputs, trpc, trpcOptions } from '@client/trpc/client';
import {
    CONFIG_LOOKUP_QUERY_OPTIONS,
    LIST_ROUTE_PREFETCH_OPTIONS,
    LIST_ROUTE_QUERY_OPTIONS,
    TRAINING_PERIODS_LIST_INPUT,
    TRAINING_PERIODS_LIST_PREFETCH_OPTIONS,
} from '@client/trpc/query-options';
import type { ShareableRouteContext } from '@client/types/router';
import { formatTrainingRecordDecimal } from '@client/utils';
import { TokenRefreshManager } from '@client/utils/auth/TokenRefreshManager';
import { useContentReadyMark } from '@client/utils/performance/content-ready';
import { DEEP_LIST_OFFSET_LIMIT, ORG_ROLES } from '@shared/constants';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { Tag } from 'antd';
import { CalendarDays, CalendarPlus } from 'lucide-react';
import { runDetailRoutePrefetchWhenAuthorised, shouldPrefetchDetailRouteData } from '../../../detail-route-prefetch';
import {
    applyTrainingFiltersToInput,
    canPrefetchFreshListInput,
    freshTrainingListInput,
    LIST_TABLE_PERSIST_KEYS,
} from '../../../fresh-list-inputs';
import { readStoredQueryParams } from '../../../list-route-prefetch';
import { TrainingFilters } from './TrainingFilters';

export const Route = createFileRoute('/console/$orgId/compliance/training/search')({
    staticData: {
        ui: {
            title: 'Training register',
            subtitle: 'Create, track, and manage training records',
            icon: ICONS.staffTraining,
        },
    },
    beforeLoad: ({ context, params, location, preload, abortController }): ShareableRouteContext => {
        // The register's LCP element only paints after this list resolves, and the
        // procedure itself is fast (32ms p75) — the cost is that the fetch cannot
        // start until mount. Start it from the router instead; no saved views here,
        // so a return visit's stored params (or the fresh-entry default) are always
        // the exact input (CUR-1036, task 2.4). The periods list is warmed too, and kept
        // through a long register dwell, so the training user page can request its
        // records in its first batch (CUR-1824). The training record configuration
        // rides the same batch so the points column header is ready with the rows.
        runDetailRoutePrefetchWhenAuthorised(
            () => {
                const authState = useAuthStore.getState();
                const user = selectUser(authState);
                // Training is hidden for Curium Lite (CUR-2686): its procedures refuse a Lite org.
                if (selectIsLiteOrg(authState)) {
                    return false;
                }
                return shouldPrefetchDetailRouteData({
                    hasValidToken: authState.hasValidToken({
                        minValidityMs: TokenRefreshManager.REFRESH_BUFFER_MS,
                    }),
                    userRoles: user?.roles,
                    allowedRoles: [
                        ORG_ROLES.USER,
                        ORG_ROLES.SHARED_EDIT_USER,
                        ORG_ROLES.ORG_ADMIN,
                        ORG_ROLES.TRAINING_MANAGER,
                    ],
                    hydratedOrg: selectOrg(authState),
                    routeOrgId: params.orgId,
                    requiredModule: 'compliance',
                });
            },
            () => {
                void context.trpcUtils.trainingPeriod.listTrainingPeriods.prefetch(
                    TRAINING_PERIODS_LIST_INPUT,
                    TRAINING_PERIODS_LIST_PREFETCH_OPTIONS,
                );
                // The points column header renders from this config; warm it with the list so it is not a second batch after mount.
                void queryClient.prefetchQuery(
                    trpcOptions.configurationItem.getConfigurationItem.queryOptions(
                        { name: 'STAFF_TRAINING_RECORD' },
                        { ...CONFIG_LOOKUP_QUERY_OPTIONS, meta: LIST_ROUTE_PREFETCH_OPTIONS.meta },
                    ),
                );
                if (!canPrefetchFreshListInput()) return;
                const input =
                    readStoredQueryParams<RouterInputs['trainingRecord']['listTrainingRecordsByUser']>(
                        location.pathname,
                    ) ?? freshTrainingListInput();
                void context.trpcUtils.trainingRecord.listTrainingRecordsByUser.prefetch(
                    input,
                    LIST_ROUTE_PREFETCH_OPTIONS,
                );
            },
            { defer: !preload, signal: abortController.signal },
        );
        return undefined;
    },
    component: TrainingRecordSearch,
});

type TrainingData = RouterOutputs['trainingRecord']['listTrainingRecordsByUser'];
type TrainingRow = NonNullable<TrainingData>['rows'][number];
type TrainingOrderBy = NonNullable<RouterInputs['trainingRecord']['listTrainingRecordsByUser']['orderBy']>;

function TrainingRecordSearch() {
    const orgId = useOrgId();
    const navigate = useNavigate();
    const { formatDate } = useFormatter();
    const { can } = usePermissions();
    const canCreateTrainingPeriod = can('training_period:create');

    const [input, setInput] = usePathState<RouterInputs['trainingRecord']['listTrainingRecordsByUser']>(
        'query-params',
        freshTrainingListInput,
    );

    const { data, isPending, error, isFetching, refetch } = trpc.trainingRecord.listTrainingRecordsByUser.useQuery(
        input,
        LIST_ROUTE_QUERY_OPTIONS,
    );

    // LCP guardrail (CUR-1036, task 0.1): the register's content is ready when the
    // list resolves — static chrome painting earlier must not move this.
    useContentReadyMark(data !== undefined);

    const config = useConfigServiceLoader((configService) => configService.fetchStaffTrainingRecord());

    const columnHelper = createColumnHelper<TrainingRow, TrainingOrderBy>();

    const columns = [
        columnHelper.accessor('userId', {
            id: 'userId',
            header: 'Employee ID',
            sortable: 'userId',
            fixed: 'left',
            hideable: false,
        }),
        columnHelper.display({
            id: 'name',
            header: 'Name',
            cell: ({ row }) => (
                <User userId={row.userId} name={row.fullName} photoUrl={row.User?.photoUrl} disabled={row.disabled} />
            ),
            hideable: false,
        }),
        columnHelper.accessor('position', {
            id: 'position',
            header: 'Position',
            sortable: 'position',
        }),
        columnHelper.accessor('employmentCommencedDate', {
            id: 'employmentCommencedDate',
            header: 'Employment commenced date',
            sortable: 'employmentCommencedDate',
            cell: ({ row }) => formatDate(row.employmentCommencedDate),
        }),
        columnHelper.display({
            id: 'status',
            header: 'Status',
            cell: ({ row }) => <StatusBadge module="state" status={row.disabled ? 'DISABLED' : 'ACTIVE'} />,
        }),
        columnHelper.accessor('disabledAt', {
            id: 'disabledAt',
            header: 'Disabled date',
            sortable: 'disabledAt',
            cell: ({ row }) => formatDate(row.disabledAt),
        }),
        columnHelper.accessor('sumOfHours', {
            id: 'sumOfHours',
            header: '# of hours this period',
            cell: ({ row }) => <Tag variant="outlined">{formatTrainingRecordDecimal(row.sumOfHours)}</Tag>,
            align: 'center',
        }),
        columnHelper.accessor('sumOfCipCredits', {
            id: 'sumOfCipCredits',
            header: `${config.data?.pointsName} this period`,
            cell: ({ row }) => <Tag variant="outlined">{formatTrainingRecordDecimal(row.sumOfCipCredits)}</Tag>,
            align: 'center',
        }),
        columnHelper.actions({
            cell: ({ row }) => (
                <Button.OpenInNewTab
                    to="/console/$orgId/compliance/training/user/$userId"
                    params={{
                        orgId,
                        userId: row.userId,
                    }}
                />
            ),
            width: 50,
        }),
    ];

    const tableProps = usePrismaTable<TrainingRow, (typeof input)['where'], TrainingOrderBy>({
        data: data?.rows || [],
        total: data?.total || 0,
        loading: isPending,
        columns,
        rowKey: 'userId',
        queryParams: input,
        onParamsChange: setInput,
        pagination: { position: 'both', maxAccessibleRecords: DEEP_LIST_OFFSET_LIMIT },
        columnVisibility: {
            enabled: true,
            persistKey: LIST_TABLE_PERSIST_KEYS.training,
            enableReordering: true,
        },
        refreshable: { enabled: true, onRefresh: () => refetch() },
        emptyContent:
            data?.hasTrainingPeriod === false ? (
                // No training period covers the current date. A missing period on a fresh org is a
                // setup step, not an error — so this is a calm get-started state. Users who can create
                // a period (training_period:create) get a CTA to the training configuration page;
                // everyone else is pointed at their administrator (CUR-417).
                <div className="mx-auto flex max-w-md flex-col items-center py-16 text-center">
                    <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-muted text-muted-foreground">
                        <CalendarDays size={26} />
                    </div>
                    <p className="font-semibold text-base text-foreground">No training period yet</p>
                    <p className="mt-2 text-muted-foreground text-sm">
                        Training records are tracked against a training period, and none covers the current date.
                        {canCreateTrainingPeriod && ' Set one up to start recording training.'}
                    </p>
                    {canCreateTrainingPeriod ? (
                        <Button.Link
                            to="/console/$orgId/configuration/compliance/training/training-periods"
                            params={{ orgId }}
                            variant="primary"
                            icon={<CalendarPlus size={16} />}
                            className="mt-5"
                        >
                            Set up a training period
                        </Button.Link>
                    ) : (
                        <p className="mt-4 text-muted-foreground text-sm">
                            Ask your <span className="font-medium text-foreground">organisation administrator</span> to
                            set one up.
                        </p>
                    )}
                </div>
            ) : undefined,
        emptyState: { noRecordsTitle: 'No training records yet' },
    });

    return (
        <PageController route={Route}>
            {error ? (
                <InlineQueryError error={error} onRetry={() => void refetch()} retrying={isFetching} />
            ) : (
                <>
                    <Outlet />

                    <TableToolbar
                        filters={
                            <TrainingFilters
                                onChange={(where, computedFilters) =>
                                    setInput((prev) => applyTrainingFiltersToInput(prev, where, computedFilters))
                                }
                            />
                        }
                        actions={
                            <Button.Add.Link
                                label="Add completed training"
                                to="/console/$orgId/compliance/training/search/add"
                                params={{ orgId }}
                                variant="primary"
                            />
                        }
                    />

                    <PrismaTable
                        {...tableProps}
                        onRow={({ userId }) => ({
                            className: 'cursor-pointer',
                            onClick: (e) =>
                                navigate(
                                    {
                                        to: '/console/$orgId/compliance/training/user/$userId',
                                        params: {
                                            orgId,
                                            userId,
                                        },
                                    },
                                    e,
                                ),
                        })}
                    />
                </>
            )}
        </PageController>
    );
}
